import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileUp, Download, BadgeCheck } from "lucide-react";
import { api, errMsg, fmtDate, downloadResume, cedi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, BeltBadge, Field } from "../../components/common";
import { MembershipCard, MemberBadge } from "../../components/Membership";

const REVIEW = { none: "", submitted: "Submitted - our team is tidying it up", reviewed: "Reviewed & polished by Dexterity Consult" };

export default function Resume() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [skills, setSkills] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/profile").then((r) => { setP(r.data); setSkills((r.data.skills || []).join(", ")); });
  useEffect(() => { load(); }, []);
  if (!p) return null;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...p, skills: skills.split(","), experience_years: Number(p.experience_years) || 0 };
      const { data } = await api.put("/profile", body);
      setP(data);
      toast.success("Profile saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/profile/resume", fd);
      setP(data);
      toast.success("Resume uploaded");
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <PageHeader eyebrow="My resume" title="Resume & profile" subtitle="Upload your resume and our team tidies it up for employers. Your Dexterity Consult certifications are added automatically when you pass."
        action={p.membership_paid && <MemberBadge />} />
      {!p.membership_paid && <div className="mb-6"><MembershipCard onPaid={load} /></div>}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="dx-card p-6 lg:col-span-2 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Headline"><input className="dx-input" value={p.headline} onChange={set("headline")} placeholder="e.g. Customer Service Officer" data-testid="profile-headline-input" /></Field>
            <Field label="Desired role"><input className="dx-input" value={p.desired_role} onChange={set("desired_role")} data-testid="profile-desired-role-input" /></Field>
            <Field label="Phone"><input className="dx-input" value={p.phone} onChange={set("phone")} data-testid="profile-phone-input" /></Field>
            <Field label="Town / City"><input className="dx-input" value={p.location} onChange={set("location")} data-testid="profile-location-input" /></Field>
            <Field label="Years of experience"><input type="number" min="0" className="dx-input" value={p.experience_years} onChange={set("experience_years")} data-testid="profile-experience-input" /></Field>
            <Field label="Availability">
              <select className="dx-input" value={p.availability} onChange={set("availability")} data-testid="profile-availability-select">
                {["Immediately", "In 2 weeks", "In 1 month", "Not available"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Skills (comma separated)"><input className="dx-input" value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="Customer Service, Excel, Driving" data-testid="profile-skills-input" /></Field>
          <Field label="Education"><input className="dx-input" value={p.education} onChange={set("education")} data-testid="profile-education-input" /></Field>
          <Field label="About me"><textarea rows={4} className="dx-input" value={p.bio} onChange={set("bio")} data-testid="profile-bio-input" /></Field>
          <button className="dx-btn-purple" onClick={save} disabled={busy} data-testid="profile-save-btn">{busy ? "Saving…" : "Save profile"}</button>
        </div>

        <div className="space-y-5">
          <div className="dx-card p-6">
            <h3 className="font-display font-semibold text-lg">Resume file</h3>
            {p.resume_filename ? (
              <div className="mt-3 text-sm" data-testid="resume-file-info">
                <div className="font-semibold break-all">{p.resume_filename}</div>
                <div className="text-xs text-[#6b5a73]">Uploaded {fmtDate(p.resume_uploaded_at)}</div>
                {REVIEW[p.resume_status] && <div className={`mt-2 text-xs font-semibold ${p.resume_status === "reviewed" ? "text-emerald-700" : "text-[#8a6a00]"}`} data-testid="resume-review-status">{REVIEW[p.resume_status]}</div>}
                <button className="dx-btn-ghost mt-3" onClick={() => downloadResume(user.id, p.resume_filename)} data-testid="resume-download-btn"><Download className="w-4 h-4" />Download</button>
              </div>
            ) : <p className="text-sm text-[#6b5a73] mt-2">No resume uploaded yet.</p>}
            {p.membership_paid ? (
              <label className="dx-btn-gold w-full mt-4 cursor-pointer" data-testid="resume-upload-label">
                <FileUp className="w-4 h-4" />{p.resume_filename ? "Replace resume" : "Upload resume"}
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={upload} data-testid="resume-file-input" />
              </label>
            ) : <div className="mt-4 text-xs font-semibold text-[#8a6a00]" data-testid="resume-locked">Join for {cedi(100)} to upload your resume.</div>}
            <p className="text-xs text-[#8a7a92] mt-2">PDF or Word, max 5MB.</p>
          </div>

          <div className="dx-card p-6" data-testid="resume-certifications">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">Certifications</h3>
              <BeltBadge belt={p.highest_belt} />
            </div>
            {p.certifications?.length ? p.certifications.map((c) => (
              <Link key={c.certificate_no} to={`/certificate/${c.certificate_no}`} className="mt-3 flex items-start gap-3 rounded-xl bg-[#FAF8FC] p-3 hover:bg-[#F4EEF7]" data-testid={`resume-cert-${c.course_slug}`}>
                <BadgeCheck className="w-5 h-5 text-[#D4AF37] shrink-0" />
                <div><div className="font-semibold text-sm">{c.title}</div><div className="text-xs text-[#6b5a73]">Dexterity Consult verified · {fmtDate(c.completed_at)}</div></div>
              </Link>
            )) : <p className="text-sm text-[#6b5a73] mt-2">Pass a course in the Academy to add your first certification.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
